'use client'

import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Card } from '@/ui/cards/card/Card'

import { Link } from '@/ui/links/link/Link.client'

export const AVAILABILITY_ISSUES = {
  StoreOnly: 'storeOnly',
  NoPricesAvailable: 'noPricesAvailable',
  IsNotSellable: 'isNotSellable',
} as const

export const I18N_KEYS_ISSUE = Object.values(AVAILABILITY_ISSUES)

export type ProductAvailabilityIssue = (typeof I18N_KEYS_ISSUE)[number]

export type ProductAvailabilityIssueCardProps = {
  /** The type of message displayed */
  issue: ProductAvailabilityIssue
  favoritePOSPhoneNumber?: string
  compact?: boolean
}

export function ProductAvailabilityIssueCard({
  issue,
  favoritePOSPhoneNumber,
  compact = false,
}: ProductAvailabilityIssueCardProps) {
  const { t } = useTranslation(['productAvailabilityIssueCard'])
  const isAuthenticated = useIsAuthenticated()

  return (
    <div data-testid="product-price-message" className="flex h-full flex-col justify-between">
      <Card
        padding="sm"
        skin="body-alt"
        radius="xs"
        className={twMerge(
          'relative flex flex-col items-center text-center',
          compact && 'max-w-16',
        )}
      >
        <p className="font-bold text-brand-1-800" data-testid="product-price-message/title">
          {t(`productAvailabilityIssueCard:${issue}`)}
        </p>
        {!compact && (
          <p data-testid="product-price-message/subtitle">
            {t(
              `productAvailabilityIssueCard:${isAuthenticated ? 'textSignedIn' : 'textNotSignedIn'}`,
            )}
          </p>
        )}
        {favoritePOSPhoneNumber && (
          // TODO: use Button when it provides an equivalent behavior for the link underline prop
          // Maybe with a class using this logic "no-underline data-[hovered]:underline data-[pressed]:underline"
          <Link
            variant="free"
            href={`tel:${favoritePOSPhoneNumber}`}
            key="contact"
            className="mt-0.5 text-lg"
            bold
            tint="brand-1"
          >
            {favoritePOSPhoneNumber}
          </Link>
        )}
      </Card>
    </div>
  )
}
